/**
 * The monitoring dashboard's period filter, in one place.
 *
 * Athira asked (2026-08-19) that the same calculation apply whichever period is
 * selected, on every screen. This used to be copy-pasted per component, and the
 * facilitator detail page had no filter at all — so it always showed all-time
 * figures while the selector on the way in said "this week". One definition here,
 * imported everywhere.
 */
// 'custom' = a specific date range the user picks on the calendar. Athira asked
// (2026-08-25) for this because the programme runs ~6 months and they want to
// go back to a specific past day's record, which week/month/all-time can't do.
export type Period = 'week' | 'month' | 'all' | 'custom';

export const PERIOD_LABEL: Record<Period, string> = {
  week: 'This week',
  month: 'This month',
  all: 'All time',
  custom: 'Custom range',
};

export const PERIODS: Period[] = ['week', 'month', 'all', 'custom'];

/** A period plus, for 'custom', the exact dates picked on the calendar. */
export interface PeriodValue {
  period: Period;
  since?: string;   // YYYY-MM-DD, only meaningful when period === 'custom'
  until?: string;   // YYYY-MM-DD, only meaningful when period === 'custom'
}

export const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

export const todayIso = (): string => isoDate(new Date());

/** `{since, until}` for the API. "This week" is Monday->today; "this month" is
 *  the 1st->today; "all time" sends nothing; "custom" sends exactly the dates
 *  picked (either end may be open). The backend decides which whole weeks /
 *  days that window can be averaged over. */
export const periodRange = (
  p: Period,
  custom?: { since?: string; until?: string },
): { since?: string; until?: string } => {
  if (p === 'custom') return { since: custom?.since || undefined, until: custom?.until || undefined };
  if (p === 'all') return {};
  const now = new Date();
  const until = isoDate(now);
  if (p === 'month') return { since: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), until };
  const monday = new Date(now); // week = Monday..today
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return { since: isoDate(monday), until };
};

/** Days of history the trend chart should draw when the period is "all time"
 *  (week/month windows already say exactly which days they cover). A whole
 *  ~6-month programme now fits, so the long-run what->how shift is visible —
 *  Athira 2026-08-25 noted a 90-day window was too short to show it. The chart
 *  buckets these daily points into weekly averages, so the extra span reads
 *  cleanly rather than as noise. */
export const trendDaysFor = (p: Period): number => (p === 'week' ? 7 : p === 'month' ? 31 : 220);

/** Whether a period is ready to fetch. 'custom' needs at least one date picked,
 *  so we don't fire a query for an empty range while the user is still typing. */
export const periodReady = (p: Period, custom?: { since?: string; until?: string }): boolean =>
  p !== 'custom' || Boolean(custom?.since || custom?.until);

/** A human phrase for the selected period, for headings/sentences. For a custom
 *  range it names the actual dates ("21 Jul to 4 Aug") instead of the generic
 *  "custom range", so the reader can see exactly what they are looking at. */
export const describePeriod = (p: Period, custom?: { since?: string; until?: string }): string => {
  if (p !== 'custom') return PERIOD_LABEL[p].toLowerCase();
  const fmt = (s?: string) =>
    s ? new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null;
  const from = fmt(custom?.since);
  const to = fmt(custom?.until);
  if (from && to) return from === to ? from : `${from} to ${to}`;
  if (from) return `since ${from}`;
  if (to) return `up to ${to}`;
  return 'a custom range';
};
